import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

const PaidAmmenities = () => {
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);

  const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails')) || {};

  useEffect(() => {
    fetch(`${BQ_BASE_URL}/bq/api/amenities`)
      .then((response) => response.json())
      .then((data) => {
        setAmenities(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching amenities:', error);
        setLoading(false);
      });
  }, []);

  const openConfirmationModal = async (service) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/preview_amenity_service_billing`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        const selectedAmenity = data.amenities.find(
          (amenity) => amenity.amenity_name === service.amenityname
        );
        setSelectedService(selectedAmenity);
        setIsConfirmationModalOpen(true);
      } else {
        toast.error('Error fetching billing details.');
      }
    } catch (error) {
      toast.error('An error occurred while fetching billing details.');
    }
  };

  const confirmServiceBooking = async () => {
    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/create_amenity_service_billing?bookingid=${bookingDetails.bookingid}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify([selectedService.amenity_name]),
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Store billing details & selected service in localStorage
        localStorage.setItem('billingDetails', JSON.stringify(data));
        localStorage.setItem('selectedService', JSON.stringify(selectedService));

        toast.success('Service confirmed successfully');
        closeConfirmationModal();
        setShowPaymentModal(true); // Show payment modal after confirmation
      } else {
        toast.error('Failed to confirm service.');
      }
    } catch (error) {
      toast.error('An error occurred while confirming the service.');
    }
  };

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    setSelectedService(null);
  };

  useEffect(() => {
    const loadRazorpayScript = () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => setRazorpayLoaded(true);
      script.onerror = () => {
        toast.error('Error loading Razorpay script. Please try again.');
      };
      document.body.appendChild(script);
    };

    loadRazorpayScript();
  }, []);

  const handlePaymentOption = async (paymentMethod) => {
    if (!razorpayLoaded) {
      toast.error('Razorpay script is not loaded yet.');
      return;
    }

    if (paymentMethod === 'payNow') {
      try {
        // Retrieve selected service from localStorage
        const selectedService = JSON.parse(localStorage.getItem('selectedService'));
        if (!selectedService) {
          toast.error('No service selected for payment.');
          return;
        }

        // Retrieve billing details from localStorage
        const billingDetails = JSON.parse(localStorage.getItem('billingDetails'));
        if (!billingDetails || !billingDetails.billingid) {
          toast.error('Billing details not found. Please try again.');
          return;
        }

        const billingId = billingDetails.billingid;
        const bookingId = bookingDetails.bookingid;

        const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${billingId}&bookingid=${bookingId}`;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });

        if (!response.ok) throw new Error('Failed to create payment order');

        const orderData = await response.json();

        if (orderData && orderData.razorpay_order_id) {
          const options = {
            key: REACT_APP_RAZORPAY_KEY,
            amount: orderData.amount,
            currency: 'INR',
            name: 'Pagoda Hotel',
            description: selectedService.amenity_name, // Show service name in payment
            order_id: orderData.razorpay_order_id,
            handler: async function (response) {
              const paymentDetails = {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              };

              try {
                const verificationResponse = await fetch(
                  `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Accept: 'application/json',
                    },
                    body: JSON.stringify(paymentDetails),
                  }
                );

                if (verificationResponse.ok) {
                  toast.success('Payment successful and verified!');
                  window.location.href = '/amenities-payment-succesful';
                } else {
                  toast.error('Payment verification failed.');
                }
              } catch (error) {
                console.error('Verification error:', error);
                toast.error('Error verifying payment. Please try again.');
              }
            },
            prefill: {
              name: `${bookingDetails.firstname} ${bookingDetails.lastname}`,
              email: bookingDetails.emailid,
              contact: bookingDetails.phonenumber.toString(),
            },
            theme: {
              color: '#3399cc',
            },
          };

          const rzp = new window.Razorpay(options);
          rzp.open();
        }
      } catch (error) {
        console.error('Payment error:', error);
        toast.error('Error processing payment. Please try again.');
      }
    } else if (paymentMethod === 'payLater') {
      toast.info('Payment deferred. Please pay at the front desk.');
      localStorage.removeItem('selectedService');
      localStorage.removeItem('billingDetails');
      setShowPaymentModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p>Loading amenities...</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen ">
      <Link
        to="/reservation/options/booking/welcome"
        className="absolute top-24 left-8 text-black hover:text-gray-800 transition font-semibold text-2xl"
      >
        <IoMdArrowRoundBack size={32} />
      </Link>
      <header className="text-white p-6 text-center">
        <h1 className="text-3xl font-bold text-center text-black ">Paid Amenities</h1>
        <h1 className="text-md font-poppins font-semibold text-center mb-1 text-black">
          Enhance your stay with our premium services tailored for your comfort and convenience.
        </h1>
        <hr className="border border-gray-400 w-1/3 mx-auto " />
      </header>
      <div className="flex justify-center items-center px-10 py-10 bg-gray-100">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 max-w-7xl w-full">
          {amenities.map((amenity) => (
            <div
              key={amenity.amenityid}
              className="bg-white shadow-md rounded-2xl overflow-hidden transition-all hover:shadow-2xl flex flex-col justify-between transform hover:-translate-y-1 duration-300 p-6"
            >
              <div className="flex flex-col flex-grow">
                <h3 className="text-xl font-semibold text-gray-900 text-start">
                  {amenity.amenityname}
                </h3>
                <p className="text-gray-600 font-semibold mt-2 text-sm text-start flex-grow">
                  {amenity.description}
                </p>
              </div>
              <p className="text-gray-700 font-semibold text-start mt-4 text-lg">
                Price: <span className="font-semibold text-black">₹{amenity.price}</span>
              </p>
              <div className="text-center pt-4">
                <button
                  onClick={() => openConfirmationModal(amenity)}
                  className="bg-black text-white px-5 py-2 rounded-lg hover:bg-gray-900 transition-all text-sm shadow-lg hover:shadow-xl"
                >
                  Book Service
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isConfirmationModalOpen && selectedService && (
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50 transition-all ease-in-out duration-300"
          onClick={closeConfirmationModal}
        >
          <div
            className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full animate__animated animate__fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <h3 className="text-2xl font-semibold text-gray-800">Order Confirmation</h3>
              <p className="text-sm text-gray-600 mt-1">You have selected the following service:</p>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="font-medium text-gray-600">Service:</p>
                  <p className="text-gray-800 ">{selectedService.amenity_name}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-600">Booking ID:</p>
                  <p className="text-gray-800">{bookingDetails.bookingid}</p>
                </div>
              </div>

              <div className="border-gray-300 pt-3 mt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="font-medium text-gray-600">Room No:</p>
                    <p className="text-gray-800">{bookingDetails.roomnumber}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-300 pt-3 mt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="font-medium text-gray-600">Base Price:</p>
                    <p className="text-lg font-semibold text-gray-800">
                      ₹{selectedService.base_amount}
                    </p>
                  </div>
                  <div>
                    <p className=" font-medium text-gray-600">Tax Amount:</p>
                    <p className="text-lg font-semibold text-gray-800">
                      ₹{selectedService.tax_amount}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="font-medium text-gray-600">Final Price:</p>
                  <p className="text-lg font-semibold text-gray-800">
                    ₹{selectedService.final_amount}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end items-center gap-4 mt-6">
              <button
                onClick={closeConfirmationModal}
                className="bg-red-500 text-white px-5 py-2 rounded-lg shadow-sm hover:text-gray-300 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmServiceBooking}
                className="bg-black text-white px-5 py-2 rounded-lg shadow-sm hover:text-gray-300 transition-all duration-200"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {showPaymentModal && (
        <div className="fixed inset-0 flex justify-center items-center bg-gray-800 bg-opacity-70 z-50">
          <div className="bg-white p-6 rounded-lg w-4/12 text-center shadow-md transform transition-all duration-200">
            <h3 className="text-2xl font-semibold mb-4 text-gray-900">Complete Your Payment</h3>
            <p className="text-md text-gray-600 mb-6">
              Your booking has been successfully confirmed! Please select a payment method to
              proceed:
            </p>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => handlePaymentOption('payLater')}
                className="bg-black text-white px-10 py-2 rounded-md hover:text-gray-200 transition-all duration-200 font-semibold"
              >
                Pay Later
              </button>
              <button
                onClick={() => handlePaymentOption('payNow')}
                className="bg-green-600 text-white px-10 py-2 rounded-md hover:text-black transition-all duration-200 font-semibold"
              >
                Pay Now
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default PaidAmmenities;
